// Single unified markdown previewer for the workspace.
//
// Workspace-project pages (tickets, docs, knowledge-base) own their page-level
// H1/H2 chrome, so user-supplied markdown must never claim those heading slots.
// When `demoteHeadings` is true (default): h1→H4, h2→H5, h3-h6→H6.
// When false: native H1-H6 are used (useful for stand-alone surfaces where the
// markdown IS the primary content).
//
// Props:
//   markdown       — the markdown source string (or an (el, s) factory)
//   text           — DEPRECATED alias for `markdown`; see the component below
//   withMentions   — default false; when true applies @mention / #channel highlighting
//   demoteHeadings — default true; h1→H4, h2→H5, h3-h6→H6
//
// Internal rendering delegates to transformMarkdown from symbolize-markdown.
// Caches on el._lastMd to skip unchanged updates.
//
// Chat-specific highlights (##ticket, @@event, +reference) are always applied
// when the body contains the respective patterns — they only fire when the regex
// matches so they are safe for non-chat surfaces (tickets, docs).

import { transformMarkdown } from '@symbo.ls/symbolize-markdown'
import { getSDK } from '../functions/sdk.js'

// ⚠ DOCUMENTED FRANK EXCEPTION (2026-08-14) — this one import is the entire
// source of this package's 14 remaining `frank to-json`
// `unresolvable-identifier` findings, and none of them can be fixed here.
// `@symbo.ls/symbolize-markdown` is on frank's `BUNDLE_INLINE_PACKAGES`
// allowlist (smbls/plugins/frank/toJSON.js) — deliberately, because stubbing
// it made a module-load-time `transformMarkdown(...)` serialize as `""` and
// blanked 96% of the docs page — so frank inlines its PUBLISHED, MINIFIED
// dist and lifts its internals into globalScope as single-letter entries
// (`globalScope.F/C/B/M/S/E/g2` — the markdown block splitter, node
// dispatcher, heading/paragraph/table builders and inline-span mapper). Their
// free variables (`q u x j v r m k`) are sibling minified declarations inside
// that same dist — all eight verified present in
// node_modules/@symbo.ls/symbolize-markdown/dist/esm/index.js — which the
// lift does not carry across. Nothing in this package's source can change
// that; it is a frank-vs-minified-dist interaction. Do not "fix" it by
// dropping the import.

// FA204/FA207 / frank trap — `applyMarkdownToElement` is genuinely called via
// `el.call('applyMarkdownToElement', …)` from tickets/TicketDetailModal.js
// (its own comment explains why: a raw cross-package import gets stubbed by
// frank's EXTERNAL_PACKAGES at serialization time, so it reaches this
// function through the runtime-merged context instead — the SAME merged
// context that also makes a bare module-scope/sibling-export read
// unresolvable once this file's exports are individually frank.toJSON'd;
// project_frank_drops_module_local_helpers, the readerVoice.js precedent).
// Both `applyMarkdownToElement` and `markdownToChildren` below are therefore
// fully self-contained — every table/regex/helper they need is a LOCAL
// declaration inside their own body, duplicated rather than shared with
// MarkdownPreview's own (separately duplicated) `scope` methods, since a
// plain arrow export has no `this`/`el.scope` of its own to route through.
//
// `highlightMentions` / `highlightChannels` go through `el.call` for the same
// reason and are NOT imported. They live in `@symbo-ls/workspace-chat`, which
// is not even a declared dependency of this package — the old raw
// `import … from '@symbo-ls/workspace-chat/functions/highlightMentions.js'`
// resolved only through outer-monorepo hoisting (a phantom dependency), and it
// also pointed the shared layer at a feature package that sits ABOVE it. At
// serialization time esbuild turned it into a namespace object frank cannot
// lift, so both reads emitted as `__scope.import_highlightMentions.<name>`
// with nothing backing them: a TypeError on the first @mention in any
// frank-served chat/ticket body (`frank to-json`:
// `scope-read-name-collision`/`import_highlightMentions`, 4 findings). Chat
// barrels both functions (`packages/chat/functions/index.js:13`), so the
// merged runtime context carries them wherever chat is linked; a host that
// does NOT link chat now simply renders the markdown without mention
// highlighting instead of throwing.

// ── Core render helper — also used by the back-compat shim ─────────────────
// Exposed as a named export so renderMarkdown.js can delegate to it without
// importing the DOMQL component (avoids circular deps). Consumers calling the
// function path (el.call / imperative) always use this helper directly.
//
// Options:
//   withMentions   — boolean (default false)
//   demoteHeadings — boolean (default true)
//   people         — array of person objects for @mention resolution
//   channels       — array of channel objects for #channel resolution
//   components     — optional custom components map (overrides the demote/native map)
// A reference badge is an icon glyph plus PLAIN TEXT, and the text half is
// server data: a ticket title, an event title, a reference type. Building it
// as an HTML string put that data through `innerHTML`, so a title carrying
// markup ran in the bubble of every reader who saw the message. The glyph is
// a constant from CARD_ICONS; the data goes in a TEXT NODE, where markup is
// characters and nothing else. Module-level on purpose: frank lifts a plain
// module-level function whole, and the serialized methods below call it.
const paintRefBadge = (span, icon, text) => {
  const glyph = span.ownerDocument.createElement('span')
  glyph.textContent = icon
  span.replaceChildren(glyph, span.ownerDocument.createTextNode(text))
}

export const applyMarkdownToElement = (el, body, emptyNode, options = {}) => {
  const DEMOTE_COMPONENTS = {
    em: 'Em',
    h1: 'H4',
    h2: 'H5',
    h3: 'H6',
    h4: 'H6',
    h5: 'H6',
    h6: 'H6',
    hr: 'Hr',
    li: 'LI',
    ol: 'OL',
    ul: 'UL',
    del: 'Del',
    img: 'Img',
    pre: 'Pre',
    code: 'InlineCode',
    link: 'Link',
    strong: 'Strong',
    codeBlock: 'CodeBlock',
    paragraph: 'P',
    blockquote: 'Blockquote'
  }
  const NATIVE_COMPONENTS = {
    em: 'Em',
    h1: 'H1',
    h2: 'H2',
    h3: 'H3',
    h4: 'H4',
    h5: 'H5',
    h6: 'H6',
    hr: 'Hr',
    li: 'LI',
    ol: 'OL',
    ul: 'UL',
    del: 'Del',
    img: 'Img',
    pre: 'Pre',
    code: 'InlineCode',
    link: 'Link',
    strong: 'Strong',
    codeBlock: 'CodeBlock',
    paragraph: 'P',
    blockquote: 'Blockquote'
  }
  // Reference-card chrome (mirrors renderMarkdown.js's own duplicate copy).
  const CARD_BASE =
    'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;vertical-align:middle;text-decoration:none;'
  const CARD_STYLES = {
    ticket: CARD_BASE + 'color:var(--color-orange,#f59e0b);background:rgba(245,158,11,0.1);',
    event: CARD_BASE + 'color:var(--color-blue,#6366f1);background:rgba(99,102,241,0.1);',
    reference: CARD_BASE + 'color:var(--color-green,#22c55e);background:rgba(34,197,94,0.1);'
  }
  const CARD_ICONS = { ticket: '■', event: '○', reference: '◆' }

  const replaceTextNodes = (targetEl, regex, makeSpan) => {
    if (!targetEl || !targetEl.node) return []
    const walker = document.createTreeWalker(targetEl.node, NodeFilter.SHOW_TEXT)
    const replacements = []
    let textNode
    while ((textNode = walker.nextNode())) {
      regex.lastIndex = 0
      if (!regex.test(textNode.textContent)) continue
      replacements.push(textNode)
    }
    const spans = []
    for (const node of replacements) {
      if (!node.parentNode) continue
      const frag = document.createDocumentFragment()
      let last = 0
      regex.lastIndex = 0
      let match
      while ((match = regex.exec(node.textContent)) !== null) {
        if (match.index > last)
          frag.appendChild(document.createTextNode(node.textContent.slice(last, match.index)))
        const span = makeSpan(match)
        spans.push(span)
        frag.appendChild(span)
        last = match.index + match[0].length
      }
      if (last < node.textContent.length)
        frag.appendChild(document.createTextNode(node.textContent.slice(last)))
      node.parentNode.replaceChild(frag, node)
    }
    return spans
  }

  // Anchored at start-of-string OR a whitespace boundary so `1##5` (inline
  // math, version strings, etc.) doesn't trigger a ticket badge.
  const TICKET_RE = /(?<=^|\s)##(\d+)/g
  const highlightTickets = (targetEl) => {
    const spans = replaceTextNodes(targetEl, TICKET_RE, (match) => {
      const num = parseInt(match[1], 10)
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.ticket
      span.setAttribute('data-ref-ticket', String(num))
      paintRefBadge(span, CARD_ICONS.ticket, ` #${num}`)
      return span
    })
    if (!spans.length) return
    const numbers = spans.map((s) => parseInt(s.getAttribute('data-ref-ticket'), 10))
    // Phase 4: sdk.tickets.list (Mongo-backed main server)
    // getService is Proxy-deferred — resolve the service first, then call list.
    Promise.resolve(getSDK().getService('tickets'))
      .then((svc) =>
        svc.list(
          { deleted: false, number_in: [...new Set(numbers)] },
          { columns: 'id,number,title,state,columnKey' }
        )
      )
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || res?.rows || []
        const map = {}
        for (const t of data || []) map[t.number] = t
        for (const span of spans) {
          const num = parseInt(span.getAttribute('data-ref-ticket'), 10)
          const ticket = map[num]
          if (ticket)
            paintRefBadge(
              span,
              CARD_ICONS.ticket,
              ` #${num} · ${(ticket.title || '').slice(0, 40)}`
            )
        }
      })
      .catch(() => {})
  }

  // Drop space from the capture class so `@@my-event with more text` only
  // matches `my-event`, not `my-event with more text`.
  const EVENT_RE = /(?<=^|\s)@@([a-zA-Z0-9._-]+)/g
  const highlightEvents = (targetEl) => {
    const spans = replaceTextNodes(targetEl, EVENT_RE, (match) => {
      const title = match[1].trim()
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.event
      span.setAttribute('data-ref-event', title)
      paintRefBadge(span, CARD_ICONS.event, ` ${title}`)
      return span
    })
    if (!spans.length) return
    const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-event')))]
    // migrated 2026-04-27 to SDK route workspaceProject.calendar/list
    getSDK()
      .execute('workspaceProject.calendar', 'list', {
        filter: { title_in: titles }
      })
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || res?.events || res?.rows || []
        const map = {}
        for (const ev of data || []) {
          if (ev?.title) map[ev.title.toLowerCase()] = ev
        }
        for (const span of spans) {
          const title = span.getAttribute('data-ref-event')
          const ev = map[title.toLowerCase()]
          if (ev && ev.date) paintRefBadge(span, CARD_ICONS.event, ` ${title} · ${ev.date}`)
        }
      })
      .catch(() => {})
  }

  const REF_RE = /(?<=^|\s)\+([a-zA-Z0-9._-]+)/g
  const highlightReferences = (targetEl) => {
    const spans = replaceTextNodes(targetEl, REF_RE, (match) => {
      const title = match[1].trim()
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.reference
      span.setAttribute('data-ref-doc', title)
      paintRefBadge(span, CARD_ICONS.reference, ` ${title}`)
      return span
    })
    if (!spans.length) return
    const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-doc')))]
    // Migrated 2026-05-19: workspaceProject.documents.notes/.kb → sdk.docs.notes/.kbArticles (Mongo).
    Promise.all([
      getSDK().execute('docs.notes', 'list', { filter: { title_in: titles } }),
      getSDK().execute('docs.kbArticles', 'list', { filter: { title_in: titles } })
    ])
      .then(([notesRes, kbRes]) => {
        const notesData = Array.isArray(notesRes)
          ? notesRes
          : notesRes?.data || notesRes?.notes || notesRes?.rows || []
        const kbData = Array.isArray(kbRes)
          ? kbRes
          : kbRes?.data || kbRes?.articles || kbRes?.rows || []
        const map = {}
        for (const n of notesData || []) {
          if (n?.title) map[n.title.toLowerCase()] = 'Note'
        }
        for (const a of kbData || []) {
          if (a?.title) map[a.title.toLowerCase()] = 'KB'
        }
        for (const span of spans) {
          const title = span.getAttribute('data-ref-doc')
          const type = map[title.toLowerCase()]
          if (type) paintRefBadge(span, CARD_ICONS.reference, ` ${title} · ${type}`)
        }
      })
      .catch(() => {})
  }

  const {
    withMentions = false,
    demoteHeadings = true,
    people,
    channels,
    components: customComponents
  } = options
  if (!el || typeof el.set !== 'function') return
  if (el._lastMd === body) return
  el._lastMd = body
  if (!body) {
    el.set(emptyNode || { tag: 'span', text: '' })
    return
  }
  const components = customComponents || (demoteHeadings ? DEMOTE_COMPONENTS : NATIVE_COMPONENTS)
  // A node that renders child nodes says `text: null`: a library's bare-key
  // component may carry demo text (see markdownToChildren below). On dev
  // system/hq every ticket paragraph that started with bold text read
  // "It was the last day for our tribe, the year ends" first.
  const ownTextOnly = (node) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return node
    const out = { ...node }
    let hasKids = false
    for (const k of Object.keys(out)) {
      const v = out[k]
      if (/^[A-Z]/.test(k) && v && typeof v === 'object' && !Array.isArray(v)) {
        hasKids = true
        out[k] = ownTextOnly(v)
      }
    }
    if (Array.isArray(out.children) && out.children.length) {
      hasKids = true
      out.children = out.children.map(ownTextOnly)
    }
    if (hasKids && out.text === undefined) out.text = null
    return out
  }
  try {
    el.set(ownTextOnly(transformMarkdown(body, { components })))
  } catch (_) {
    el.set({ tag: 'span', text: body })
  }
  requestAnimationFrame(() => {
    // Run longer-prefix patterns FIRST (## before #, @@ before @).
    TICKET_RE.lastIndex = 0
    if (TICKET_RE.test(body)) highlightTickets(el)

    EVENT_RE.lastIndex = 0
    if (EVENT_RE.test(body)) highlightEvents(el)

    REF_RE.lastIndex = 0
    if (REF_RE.test(body)) highlightReferences(el)

    if (withMentions) {
      const MENTION_RE = /(?<=^|\s)@([a-zA-Z0-9._-]+)/g
      MENTION_RE.lastIndex = 0
      if (MENTION_RE.test(body)) el.call('highlightMentions', el, people)

      const CHANNEL_RE = /(?<=^|\s)#([a-zA-Z0-9._-]+)/g
      CHANNEL_RE.lastIndex = 0
      if (CHANNEL_RE.test(body)) el.call('highlightChannels', el, channels)
    }
  })
}

// ── Children-factory render (for reactive lists) ──────────────────────────
// Counterpart to applyMarkdownToElement for contexts where el.set's parent
// re-eval is unsafe — notably the simone chat message list, where el.set wipes
// the subtree in v3.14 (see MarketplaceDetailMarkdownBody). Returns a DOMQL
// CHILDREN array instead of mutating the element. Centralizes the demote map +
// the transformMarkdown→children normalizer + the plain-text fallback so chat /
// marketplace / preview don't each re-roll it.
//
//   options.demoteHeadings — boolean (default true)
//   options.components     — optional custom map (overrides demote/native)
export const markdownToChildren = (body, options = {}) => {
  const DEMOTE_COMPONENTS = {
    em: 'Em',
    h1: 'H4',
    h2: 'H5',
    h3: 'H6',
    h4: 'H6',
    h5: 'H6',
    h6: 'H6',
    hr: 'Hr',
    li: 'LI',
    ol: 'OL',
    ul: 'UL',
    del: 'Del',
    img: 'Img',
    pre: 'Pre',
    code: 'InlineCode',
    link: 'Link',
    strong: 'Strong',
    codeBlock: 'CodeBlock',
    paragraph: 'P',
    blockquote: 'Blockquote'
  }
  const NATIVE_COMPONENTS = {
    em: 'Em',
    h1: 'H1',
    h2: 'H2',
    h3: 'H3',
    h4: 'H4',
    h5: 'H5',
    h6: 'H6',
    hr: 'Hr',
    li: 'LI',
    ol: 'OL',
    ul: 'UL',
    del: 'Del',
    img: 'Img',
    pre: 'Pre',
    code: 'InlineCode',
    link: 'Link',
    strong: 'Strong',
    codeBlock: 'CodeBlock',
    paragraph: 'P',
    blockquote: 'Blockquote'
  }
  if (!body) return []
  const { demoteHeadings = true, components: customComponents } = options
  const components = customComponents || (demoteHeadings ? DEMOTE_COMPONENTS : NATIVE_COMPONENTS)
  // A node that renders child nodes has no text of its own, and says so. The
  // component keys above are BARE keys, so they resolve through the linked
  // libraries too, and a library's component may carry demo text: in
  // system/default `P`, `Caption` and `H1`–`H6` read "It was the last day for
  // our tribe, the year ends", `Link` reads "Link", `Strong` "Strong text".
  // A paragraph whose words all sit in child nodes (`**Subject**` alone, or
  // span + strong + span) has no `text`, so it showed that sentence before
  // its own words (dev, system/hq chat, 2026-10-28). `text: null` cancels an
  // inherited text — the rule renderAssistantMarkdown already applies.
  const ownTextOnly = (node) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return node
    const out = { ...node }
    let hasKids = false
    for (const k of Object.keys(out)) {
      const v = out[k]
      if (/^[A-Z]/.test(k) && v && typeof v === 'object' && !Array.isArray(v)) {
        hasKids = true
        out[k] = ownTextOnly(v)
      }
    }
    if (Array.isArray(out.children) && out.children.length) {
      hasKids = true
      out.children = out.children.map(ownTextOnly)
    }
    if (hasKids && out.text === undefined) out.text = null
    return out
  }
  try {
    const spec = transformMarkdown(body, { components })
    if (!spec || typeof spec !== 'object') return [{ tag: 'span', text: body }]
    // transformMarkdown returns either a single node ({tag|extends|text|children})
    // or a keyed object of top-level nodes — normalize both to a children array.
    if ('extends' in spec || 'tag' in spec || 'text' in spec || 'children' in spec) {
      return [ownTextOnly(spec)]
    }
    return Object.keys(spec)
      .filter((k) => !k.startsWith('__'))
      .map((k) => spec[k])
      .filter((v) => v && typeof v === 'object')
      .map(ownTextOnly)
  } catch (_) {
    return [{ tag: 'pre', whiteSpace: 'pre-wrap', text: body }]
  }
}

// ── Reference cards for children-rendered markdown ────────────────────────────
// `applyMarkdownToElement` paints the body AND the ##ticket / @@event / +doc
// cards in one imperative pass. A reactive `children:` factory paints the body
// itself, so the card pass has to be callable on its own — that is this.
//
// Self-contained on purpose: every helper is an inner closure, so nothing here
// depends on a module-scope free variable that frank serialization would stub
// (Rule 33). Register it and reach it with el.call('highlightMarkdownRefs', …).
//
// Call from onContentUpdate, i.e. AFTER the children are in the DOM. No rAF:
// the nodes already exist, and deferring would risk painting cards onto a row
// that a channel switch has meanwhile rebound to a different message.
export const highlightMarkdownRefs = (el, body) => {
  if (!el || !el.node || !body) return

  const CARD_BASE =
    'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;vertical-align:middle;text-decoration:none;'
  const CARD_STYLES = {
    ticket: CARD_BASE + 'color:var(--color-orange,#f59e0b);background:rgba(245,158,11,0.1);',
    event: CARD_BASE + 'color:var(--color-blue,#6366f1);background:rgba(99,102,241,0.1);',
    reference: CARD_BASE + 'color:var(--color-green,#22c55e);background:rgba(34,197,94,0.1);'
  }
  const CARD_ICONS = { ticket: '■', event: '○', reference: '◆' }

  const replaceTextNodes = (targetEl, regex, makeSpan) => {
    if (!targetEl || !targetEl.node) return []
    const walker = document.createTreeWalker(targetEl.node, NodeFilter.SHOW_TEXT)
    const replacements = []
    let textNode
    while ((textNode = walker.nextNode())) {
      regex.lastIndex = 0
      if (!regex.test(textNode.textContent)) continue
      replacements.push(textNode)
    }
    const spans = []
    for (const node of replacements) {
      if (!node.parentNode) continue
      const frag = document.createDocumentFragment()
      let last = 0
      regex.lastIndex = 0
      let match
      while ((match = regex.exec(node.textContent)) !== null) {
        if (match.index > last)
          frag.appendChild(document.createTextNode(node.textContent.slice(last, match.index)))
        const span = makeSpan(match)
        spans.push(span)
        frag.appendChild(span)
        last = match.index + match[0].length
      }
      if (last < node.textContent.length)
        frag.appendChild(document.createTextNode(node.textContent.slice(last)))
      node.parentNode.replaceChild(frag, node)
    }
    return spans
  }

  // Anchored at start-of-string OR a whitespace boundary so `1##5` (inline
  // math, version strings, etc.) doesn't trigger a ticket badge.
  const TICKET_RE = /(?<=^|\s)##(\d+)/g
  const highlightTickets = (targetEl) => {
    const spans = replaceTextNodes(targetEl, TICKET_RE, (match) => {
      const num = parseInt(match[1], 10)
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.ticket
      span.setAttribute('data-ref-ticket', String(num))
      paintRefBadge(span, CARD_ICONS.ticket, ` #${num}`)
      return span
    })
    if (!spans.length) return
    const numbers = spans.map((s) => parseInt(s.getAttribute('data-ref-ticket'), 10))
    // getService is Proxy-deferred — resolve the service first, then call list.
    Promise.resolve(getSDK().getService('tickets'))
      .then((svc) =>
        svc.list(
          { deleted: false, number_in: [...new Set(numbers)] },
          { columns: 'id,number,title,state,columnKey' }
        )
      )
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || res?.rows || []
        const map = {}
        for (const t of data || []) map[t.number] = t
        for (const span of spans) {
          if (!span.isConnected) continue
          const num = parseInt(span.getAttribute('data-ref-ticket'), 10)
          const ticket = map[num]
          if (ticket)
            paintRefBadge(
              span,
              CARD_ICONS.ticket,
              ` #${num} · ${(ticket.title || '').slice(0, 40)}`
            )
        }
      })
      .catch(() => {})
  }

  // Drop space from the capture class so `@@my-event with more text` only
  // matches `my-event`, not `my-event with more text`.
  const EVENT_RE = /(?<=^|\s)@@([a-zA-Z0-9._-]+)/g
  const highlightEvents = (targetEl) => {
    const spans = replaceTextNodes(targetEl, EVENT_RE, (match) => {
      const title = match[1].trim()
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.event
      span.setAttribute('data-ref-event', title)
      paintRefBadge(span, CARD_ICONS.event, ` ${title}`)
      return span
    })
    if (!spans.length) return
    const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-event')))]
    getSDK()
      .execute('workspaceProject.calendar', 'list', {
        filter: { title_in: titles }
      })
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || res?.events || res?.rows || []
        const map = {}
        for (const ev of data || []) {
          if (ev?.title) map[ev.title.toLowerCase()] = ev
        }
        for (const span of spans) {
          if (!span.isConnected) continue
          const title = span.getAttribute('data-ref-event')
          const ev = map[title.toLowerCase()]
          if (ev && ev.date) paintRefBadge(span, CARD_ICONS.event, ` ${title} · ${ev.date}`)
        }
      })
      .catch(() => {})
  }

  const REF_RE = /(?<=^|\s)\+([a-zA-Z0-9._-]+)/g
  const highlightReferences = (targetEl) => {
    const spans = replaceTextNodes(targetEl, REF_RE, (match) => {
      const title = match[1].trim()
      const span = document.createElement('span')
      span.style.cssText = CARD_STYLES.reference
      span.setAttribute('data-ref-doc', title)
      paintRefBadge(span, CARD_ICONS.reference, ` ${title}`)
      return span
    })
    if (!spans.length) return
    const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-doc')))]
    Promise.all([
      getSDK().execute('docs.notes', 'list', { filter: { title_in: titles } }),
      getSDK().execute('docs.kbArticles', 'list', { filter: { title_in: titles } })
    ])
      .then(([notesRes, kbRes]) => {
        const notesData = Array.isArray(notesRes)
          ? notesRes
          : notesRes?.data || notesRes?.notes || notesRes?.rows || []
        const kbData = Array.isArray(kbRes)
          ? kbRes
          : kbRes?.data || kbRes?.articles || kbRes?.rows || []
        const map = {}
        for (const n of notesData || []) {
          if (n?.title) map[n.title.toLowerCase()] = 'Note'
        }
        for (const a of kbData || []) {
          if (a?.title) map[a.title.toLowerCase()] = 'KB'
        }
        for (const span of spans) {
          if (!span.isConnected) continue
          const title = span.getAttribute('data-ref-doc')
          const type = map[title.toLowerCase()]
          if (type) paintRefBadge(span, CARD_ICONS.reference, ` ${title} · ${type}`)
        }
      })
      .catch(() => {})
  }

  // Longer-prefix patterns FIRST (## before #, @@ before @) — the mention and
  // channel passes that run after this one match the single-char prefixes.
  TICKET_RE.lastIndex = 0
  if (TICKET_RE.test(body)) highlightTickets(el)
  EVENT_RE.lastIndex = 0
  if (EVENT_RE.test(body)) highlightEvents(el)
  REF_RE.lastIndex = 0
  if (REF_RE.test(body)) highlightReferences(el)
}

// ── DOMQL v3.14 component ─────────────────────────────────────────────────────
// Props:
//   markdown       — markdown source: a string, or an (el, s) factory
//   text           — DEPRECATED alias for `markdown` (see onInit below)
//   withMentions   — boolean (default false)
//   demoteHeadings — boolean (default true)
//
// THE SOURCE PROP IS `markdown`, NOT `text`
// (SHARED-MARKDOWNPREVIEW-TEXT-PROP-RENDERS-RAW-1). `text` is a DOMQL RENDER
// prop: the framework paints it into the element's own text node
// (core.js writeElementText) and `el.set()`'s removeContent() clears the
// `content` child but never that text node — so a source read off `el.text`
// showed the body TWICE, raw source first and rendered markdown after. A
// FUNCTION source was worse: `text` is framework-reserved, so it is never
// wrapped into an evaluating getter (Rule 70), `render` read the FUNCTION
// itself, `transformMarkdown` threw, and the plain-text catch rendered no
// markdown at all while a state update moved only the inner copy. Live on
// prod shell 98a448021 the stock CMS editor Preview read
// `English text **bold**.English text **bold**.`
//
// `markdown` is a plain lowercase custom prop, so the framework never renders
// it and auto-invokes a function value on read (Rule 70) — one prop covers
// both the static and the factory form.
//
// A FACTORY SOURCE FOLLOWS STATE THROUGH `stateDeps`, below. The render is
// imperative (`el.set`), so the framework has no effect of its own reading
// the source and `onUpdate` alone never fires for a root-state change on a
// nested element. The children-factory model WOULD auto-track, but the
// ##ticket/@@event/+reference pass paints imperatively INTO the rendered
// nodes and an index-reconciled child is patched in place rather than
// rebuilt — the badge spans would survive the patch and double the body.
// `el.set` rebuilds the whole content child, which is why it stays.
//
// The component relies on state to pass people/channels for mention highlighting.
// In chat contexts state carries s.root.people / s.root.channels automatically.
// In non-chat contexts those are simply absent and withMentions stays false.

export const MarkdownPreview = {
  // FA204 / frank trap — the render logic used to be a bare module-scope
  // `_render` (assigned directly to onCreate/onUpdate) that called the
  // exported `applyMarkdownToElement` — a free-variable reference that
  // silently reads as undefined once this component is frank.toJSON'd for
  // platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). `applyMarkdownToElement` /
  // `markdownToChildren` above are now FULLY SELF-CONTAINED exports (see
  // their own header comment) rather than shared with these `scope`
  // methods — `applyMarkdownToElement` is genuinely reached via
  // `el.call('applyMarkdownToElement', …)` from tickets/TicketDetailModal.js
  // (a DIFFERENT top-level export can't route through THIS component's
  // scope anyway), so both halves independently duplicate the same
  // render pipeline (regexes, card styles/icons, the three
  // reference-highlighters). This half is called via
  // `el.scope.<name>`/`this.<name>`, so MarkdownPreview's own
  // onCreate/onUpdate resolve post-frank without touching the exported
  // utility's call graph.
  scope: {
    DEMOTE_COMPONENTS: {
      em: 'Em',
      h1: 'H4',
      h2: 'H5',
      h3: 'H6',
      h4: 'H6',
      h5: 'H6',
      h6: 'H6',
      hr: 'Hr',
      li: 'LI',
      ol: 'OL',
      ul: 'UL',
      del: 'Del',
      img: 'Img',
      pre: 'Pre',
      code: 'InlineCode',
      link: 'Link',
      strong: 'Strong',
      codeBlock: 'CodeBlock',
      paragraph: 'P',
      blockquote: 'Blockquote'
    },
    NATIVE_COMPONENTS: {
      em: 'Em',
      h1: 'H1',
      h2: 'H2',
      h3: 'H3',
      h4: 'H4',
      h5: 'H5',
      h6: 'H6',
      hr: 'Hr',
      li: 'LI',
      ol: 'OL',
      ul: 'UL',
      del: 'Del',
      img: 'Img',
      pre: 'Pre',
      code: 'InlineCode',
      link: 'Link',
      strong: 'Strong',
      codeBlock: 'CodeBlock',
      paragraph: 'P',
      blockquote: 'Blockquote'
    },
    CARD_STYLES: {
      ticket:
        'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;vertical-align:middle;text-decoration:none;color:var(--color-orange,#f59e0b);background:rgba(245,158,11,0.1);',
      event:
        'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;vertical-align:middle;text-decoration:none;color:var(--color-blue,#6366f1);background:rgba(99,102,241,0.1);',
      reference:
        'display:inline-flex;align-items:center;gap:6px;padding:3px 8px;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;vertical-align:middle;text-decoration:none;color:var(--color-green,#22c55e);background:rgba(34,197,94,0.1);'
    },
    CARD_ICONS: { ticket: '■', event: '○', reference: '◆' },
    // Anchored at start-of-string OR a whitespace boundary so `1##5` (inline
    // math, version strings, etc.) doesn't trigger a ticket badge. The
    // negative-lookahead `(?!\s)` after the trigger prevents `## foo` (with
    // space after the trigger) from greedy-matching the rest of a sentence
    // into a single badge — empirically that produced labels like
    // "send flow verified end-to-end" out of "+ send flow verified ...".
    TICKET_RE: /(?<=^|\s)##(\d+)/g,
    // Drop space from the capture class so `@@my-event with more text` only
    // matches `my-event`, not `my-event with more text`. The composer's
    // autocomplete inserts the title's slug (alphanumeric + dot/underscore/
    // dash), so multi-word titles aren't broken by this; free-form typing
    // gracefully stops at the first space.
    EVENT_RE: /(?<=^|\s)@@([a-zA-Z0-9._-]+)/g,
    REF_RE: /(?<=^|\s)\+([a-zA-Z0-9._-]+)/g,

    replaceTextNodes(el, regex, makeSpan) {
      if (!el || !el.node) return []
      const walker = document.createTreeWalker(el.node, NodeFilter.SHOW_TEXT)
      const replacements = []
      let textNode
      while ((textNode = walker.nextNode())) {
        regex.lastIndex = 0
        if (!regex.test(textNode.textContent)) continue
        replacements.push(textNode)
      }
      const spans = []
      for (const node of replacements) {
        if (!node.parentNode) continue
        const frag = document.createDocumentFragment()
        let last = 0
        regex.lastIndex = 0
        let match
        while ((match = regex.exec(node.textContent)) !== null) {
          if (match.index > last)
            frag.appendChild(document.createTextNode(node.textContent.slice(last, match.index)))
          const span = makeSpan(match)
          spans.push(span)
          frag.appendChild(span)
          last = match.index + match[0].length
        }
        if (last < node.textContent.length)
          frag.appendChild(document.createTextNode(node.textContent.slice(last)))
        node.parentNode.replaceChild(frag, node)
      }
      return spans
    },

    highlightTickets(el) {
      const spans = this.replaceTextNodes(el, this.TICKET_RE, (match) => {
        const num = parseInt(match[1], 10)
        const span = document.createElement('span')
        span.style.cssText = this.CARD_STYLES.ticket
        span.setAttribute('data-ref-ticket', String(num))
        paintRefBadge(span, this.CARD_ICONS.ticket, ` #${num}`)
        return span
      })
      if (!spans.length) return
      const numbers = spans.map((s) => parseInt(s.getAttribute('data-ref-ticket'), 10))
      // Phase 4: sdk.tickets.list (Mongo-backed main server)
      // getService is Proxy-deferred — resolve the service first, then call list.
      Promise.resolve(getSDK().getService('tickets'))
        .then((svc) =>
          svc.list(
            { deleted: false, number_in: [...new Set(numbers)] },
            { columns: 'id,number,title,state,columnKey' }
          )
        )
        .then((res) => {
          const data = Array.isArray(res) ? res : res?.data || res?.rows || []
          const map = {}
          for (const t of data || []) map[t.number] = t
          for (const span of spans) {
            const num = parseInt(span.getAttribute('data-ref-ticket'), 10)
            const ticket = map[num]
            if (ticket)
              paintRefBadge(
                span,
                this.CARD_ICONS.ticket,
                ` #${num} · ${(ticket.title || '').slice(0, 40)}`
              )
          }
        })
        .catch(() => {})
    },

    highlightEvents(el) {
      const spans = this.replaceTextNodes(el, this.EVENT_RE, (match) => {
        const title = match[1].trim()
        const span = document.createElement('span')
        span.style.cssText = this.CARD_STYLES.event
        span.setAttribute('data-ref-event', title)
        paintRefBadge(span, this.CARD_ICONS.event, ` ${title}`)
        return span
      })
      if (!spans.length) return
      const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-event')))]
      // migrated 2026-04-27 to SDK route workspaceProject.calendar/list
      getSDK()
        .execute('workspaceProject.calendar', 'list', {
          filter: { title_in: titles }
        })
        .then((res) => {
          const data = Array.isArray(res) ? res : res?.data || res?.events || res?.rows || []
          const map = {}
          for (const ev of data || []) {
            if (ev?.title) map[ev.title.toLowerCase()] = ev
          }
          for (const span of spans) {
            const title = span.getAttribute('data-ref-event')
            const ev = map[title.toLowerCase()]
            if (ev && ev.date) paintRefBadge(span, this.CARD_ICONS.event, ` ${title} · ${ev.date}`)
          }
        })
        .catch(() => {})
    },

    highlightReferences(el) {
      const spans = this.replaceTextNodes(el, this.REF_RE, (match) => {
        const title = match[1].trim()
        const span = document.createElement('span')
        span.style.cssText = this.CARD_STYLES.reference
        span.setAttribute('data-ref-doc', title)
        paintRefBadge(span, this.CARD_ICONS.reference, ` ${title}`)
        return span
      })
      if (!spans.length) return
      const titles = [...new Set(spans.map((s) => s.getAttribute('data-ref-doc')))]
      // Migrated 2026-05-19: workspaceProject.documents.notes/.kb → sdk.docs.notes/.kbArticles (Mongo).
      // sdk.docs.*.list() returns {data, count} — unwrap the data array.
      Promise.all([
        getSDK().execute('docs.notes', 'list', { filter: { title_in: titles } }),
        getSDK().execute('docs.kbArticles', 'list', { filter: { title_in: titles } })
      ])
        .then(([notesRes, kbRes]) => {
          const notesData = Array.isArray(notesRes)
            ? notesRes
            : notesRes?.data || notesRes?.notes || notesRes?.rows || []
          const kbData = Array.isArray(kbRes)
            ? kbRes
            : kbRes?.data || kbRes?.articles || kbRes?.rows || []
          const map = {}
          for (const n of notesData || []) {
            if (n?.title) map[n.title.toLowerCase()] = 'Note'
          }
          for (const a of kbData || []) {
            if (a?.title) map[a.title.toLowerCase()] = 'KB'
          }
          for (const span of spans) {
            const title = span.getAttribute('data-ref-doc')
            const type = map[title.toLowerCase()]
            if (type) paintRefBadge(span, this.CARD_ICONS.reference, ` ${title} · ${type}`)
          }
        })
        .catch(() => {})
    },

    // el.markdown is the resolved markdown source (v3.14 flat prop access) —
    // already a string when the consumer passed a factory, because a lowercase
    // custom prop is auto-invoked on read. The typeof guard covers a host that
    // assigns the prop after create, where no getter wraps it.
    // withMentions / demoteHeadings are booleans set by the consumer.
    render(el, s) {
      const src = el.markdown
      const body = typeof src === 'function' ? src(el, s) || '' : src == null ? '' : String(src)
      const withMentions = !!el.withMentions
      const demoteHeadings = el.demoteHeadings !== false
      const people = s && s.root && s.root.people
      const channels = s && s.root && s.root.channels
      if (!el || typeof el.set !== 'function') return
      if (el._lastMd === body) return
      el._lastMd = body
      if (!body) {
        el.set({ tag: 'span', text: '' })
        return
      }
      const components = demoteHeadings ? this.DEMOTE_COMPONENTS : this.NATIVE_COMPONENTS
      // A node that renders child nodes says `text: null`, so a library's
      // demo text never shows (see markdownToChildren above).
      const ownTextOnly = (node) => {
        if (!node || typeof node !== 'object' || Array.isArray(node)) return node
        const out = { ...node }
        let hasKids = false
        for (const k of Object.keys(out)) {
          const v = out[k]
          if (/^[A-Z]/.test(k) && v && typeof v === 'object' && !Array.isArray(v)) {
            hasKids = true
            out[k] = ownTextOnly(v)
          }
        }
        if (Array.isArray(out.children) && out.children.length) {
          hasKids = true
          out.children = out.children.map(ownTextOnly)
        }
        if (hasKids && out.text === undefined) out.text = null
        return out
      }
      try {
        el.set(ownTextOnly(transformMarkdown(body, { components })))
      } catch (_) {
        el.set({ tag: 'span', text: body })
      }
      requestAnimationFrame(() => {
        // Run longer-prefix patterns FIRST (## before #, @@ before @).
        this.TICKET_RE.lastIndex = 0
        if (this.TICKET_RE.test(body)) this.highlightTickets(el)

        this.EVENT_RE.lastIndex = 0
        if (this.EVENT_RE.test(body)) this.highlightEvents(el)

        this.REF_RE.lastIndex = 0
        if (this.REF_RE.test(body)) this.highlightReferences(el)

        if (withMentions) {
          const MENTION_RE = /(?<=^|\s)@([a-zA-Z0-9._-]+)/g
          MENTION_RE.lastIndex = 0
          if (MENTION_RE.test(body)) el.call('highlightMentions', el, people)

          const CHANNEL_RE = /(?<=^|\s)#([a-zA-Z0-9._-]+)/g
          CHANNEL_RE.lastIndex = 0
          if (CHANNEL_RE.test(body)) el.call('highlightChannels', el, channels)
        }
      })
    }
  },
  tag: 'div',
  // The deprecated `text` alias is moved onto `markdown` HERE — onInit runs
  // before the node exists (core.js create(), triggerLifecycle('Init') precedes
  // cacheNode), so the framework never paints the raw source and never
  // registers a text effect for it. That keeps every ALREADY PUBLISHED
  // consumer correct without a republish — the stock system/cms-app editor
  // Preview passes `text`.
  onInit: (el, s) => {
    if (el.text == null) return
    if (el.markdown == null) el.markdown = el.text
    el.text = null
  },
  // A FACTORY SOURCE FOLLOWS STATE FROM HERE, with nothing asked of the call
  // site. The framework reads every `stateDeps` selector inside its OWN
  // effect, so reading `el.markdown` invokes the consumer's factory and
  // subscribes to whatever root-state signals that factory touches; a changed
  // snapshot then fires `onStateUpdate`. This is what keeps the stock
  // system/cms-app editor Preview live as the draft is typed — it passes a
  // function on the deprecated `text` alias and declares no subscription of
  // its own. A static source yields a constant selector and never fires.
  stateDeps: [(el, s) => el.markdown],
  onStateUpdate: (el, s) => el.scope.render(el, s),
  onCreate: (el, s) => el.scope.render(el, s),
  onUpdate: (el, s) => el.scope.render(el, s)
}
